"use client";

import React, { useState, useEffect } from "react";
import Image, { type StaticImageData } from "next/image";

type AwardSlide = {
  year: string;
  image: string | StaticImageData;
  description?: React.ReactNode;
  hyperlink?: string;
};

interface AwardsSliderProps {
  eyebrow: string;
  title: React.ReactNode;
  description: React.ReactNode;
  slides: AwardSlide[];
  viewAllHref?: string;
}

export default function AwardsSlider({
  eyebrow,
  title,
  description,
  slides,
  viewAllHref = "#",
}: AwardsSliderProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const total = slides.length;

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const goPrev = () => {
    setActiveIndex((prev) => (prev - 1 + total) % total);
  };

  const goNext = () => {
    setActiveIndex((prev) => (prev + 1) % total);
  };

  const getOffset = (index: number) => {
    let diff = index - activeIndex;

    if (diff > total / 2) diff -= total;
    if (diff < -total / 2) diff += total;

    return diff;
  };

  const cardWidth = isMobile ? 260 : 380;
  const activeCardHeight = isMobile ? 400 : 500;
  const inactiveCardHeight = isMobile ? 300 : 370;
  const offsetSpacing = isMobile ? 220 : 400;
  const sliderHeight = isMobile ? 440 : 560;

  return (
    <section
      className="relative z-2 py-[50px] lg:py-[100px] overflow-hidden"
      style={{
        background: `
          radial-gradient(
            circle at 0% 0%,
            rgba(34, 91, 212, 0.10) 0%,
            rgba(34, 91, 212, 0.04) 18%,
            transparent 35%
          ),
          radial-gradient(
            circle at 100% 100%,
            rgba(34, 91, 212, 0.10) 0%,
            rgba(34, 91, 212, 0.04) 18%,
            transparent 35%
          ),
          #00040B
        `,
      }}
    >
      {/* Header */}
      <div className="relative max-w-[2500px] w-[calc(100vw_-_10%)] mx-auto px-4">
        <div className="flex flex-col lg:flex-row gap-8 lg:items-center justify-between">
          <div className="max-w-[800px]">
            <p className="text-white text-[16px] font-medium">{eyebrow}</p>

            <h2 className="mt-4 text-[length:var(--section-title)] lg:w-[13ch] w-full leading-[120%] text-white clash-font font-medium">
              {title}
            </h2>
          </div>

          <div className="gap-5 flex flex-col items-start">
            <p className="text-[#949596] text-[clamp(0.9375rem,0.4664rem+0.9815vw,2rem)] leading-[140%] max-w-[845px]">
              {description}
            </p>

            <a
              href={viewAllHref}
              className="inline-flex items-center gap-2 text-white text-[16px] md:text-[18px] font-medium hover:text-[#225BD4] transition-colors duration-300"
            >
              View All
              <span className="text-[16px]">→</span>
            </a>
          </div>
        </div>
      </div>

      {/* Slider */}
      <div className="relative max-w-7xl mx-auto px-4">
        <div
          className="mt-12 relative flex items-center justify-center w-full overflow-hidden"
          style={{
            height: `${sliderHeight}px`,
            maskImage:
              "linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%)",
            WebkitMaskImage:
              "linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%)",
          }}
        >
          {slides.map((slide, index) => {
            const offset = getOffset(index);

            if (Math.abs(offset) > 2) return null;

            const isActive = offset === 0;

            const card = (
              <div
                className="relative rounded-[20px] md:rounded-[28px] p-5 md:p-8 flex flex-col overflow-hidden transition-all duration-500 isolate"
                style={{
                  width: `${cardWidth}px`,
                  height: `${isActive ? activeCardHeight : inactiveCardHeight}px`,
                  boxShadow: isActive
                    ? "0 20px 60px -15px rgba(34,91,212,0.12)"
                    : undefined,
                  background: isActive
                    ? "linear-gradient(160deg, rgba(34,91,212,0.025) 0%, rgba(17,18,20,0.98) 45%, #0c0d0f 100%)"
                    : "linear-gradient(160deg, rgba(34,91,212,0.008) 0%, #111214 50%, #0c0d0f 100%)",
                  border: isActive
                    ? "1px solid rgba(34,91,212,0.18)"
                    : "1px solid rgba(255,255,255,0.08)",
                }}
              >
                {isActive && (
                  <div
                    className="pointer-events-none absolute -top-10 left-1/2 -translate-x-1/2 w-[220px] h-[140px] rounded-full opacity-50"
                    style={{
                      background: "#225BD4",
                      filter: "blur(60px)",
                    }}
                  />
                )}

                <div className="relative z-[1] flex items-center justify-between">
                  <span className="text-white/70 text-[14px] md:text-[18px] tracking-wide">
                    {slide.year}
                  </span>
                </div>

                <div className="relative z-[1] flex-1 flex items-center justify-center">
                  <Image
                    src={slide.image}
                    alt={slide.year}
                    width={200}
                    height={200}
                    className="object-contain w-auto drop-shadow-[0_10px_25px_rgba(0,0,0,0.5)]"
                    style={{
                      maxHeight: isMobile ? "120px" : "180px",
                    }}
                  />
                </div>

                {isActive && slide.description && (
                  <p className="relative z-[1] text-white text-[13px] md:text-[18px] leading-[150%] underline decoration-[#225BD4]/60 underline-offset-4 text-center mt-4">
                    {slide.description}
                  </p>
                )}
              </div>
            );

            return (
              <div
                key={index}
                onClick={() => {
                  if (!slide.hyperlink) {
                    setActiveIndex(index);
                  }
                }}
                className="absolute transition-all duration-500 ease-out cursor-pointer group isolate"
                style={{
                  transform: `translateX(${offset * offsetSpacing}px) scale(${
                    isActive ? 1 : Math.abs(offset) === 1 ? 0.9 : 0.8
                  })`,
                  opacity: isActive ? 1 : Math.abs(offset) === 1 ? 0.6 : 0.3,
                  zIndex: 10 - Math.abs(offset),
                }}
              >
                {slide.hyperlink ? (
                  <a
                    href={slide.hyperlink}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    aria-label={`View ${slide.description || slide.year}`}
                  >
                    {card}
                  </a>
                ) : (
                  card
                )}
              </div>
            );
          })}
        </div>

        {/* Arrows */}
        <div className="mt-10 flex items-center justify-center gap-4">
          <button
            type="button"
            onClick={goPrev}
            aria-label="Previous"
            className="w-12 h-12 md:w-14 md:h-14 md:text-[24px] cursor-pointer rounded-full border border-white/20 flex items-center justify-center text-white hover:bg-[#225BD4]/20 hover:border-[#225BD4]/50 transition-all duration-300"
          >
            ←
          </button>

          <button
            type="button"
            onClick={goNext}
            aria-label="Next"
            className="w-12 h-12 md:w-14 md:h-14 md:text-[24px] cursor-pointer rounded-full border border-white/20 flex items-center justify-center text-white hover:bg-[#225BD4]/20 hover:border-[#225BD4]/50 transition-all duration-300"
          >
            →
          </button>
        </div>
      </div>
    </section>
  );
}
